// /orgs/$orgSlug — LAYOUT route for everything org-scoped.
//
// Every `orgs.$orgSlug.*` file route nests under this one, so this
// component must render an <Outlet/> — and only an <Outlet/> — or
// child navigations update the URL while the screen keeps showing the
// parent (the B9 dead-click bug). The actual org landing page lives in
// `orgs.$orgSlug.index.tsx`.
//
// The route-level `errorComponent` stays here so a 404 / 403 from any
// org-scoped surface gets a useful boundary instead of the global one.
//
// Auth is enforced by the `__root.tsx` `beforeLoad` guard, so by the
// time this route renders we have a session.

import { createFileRoute, Link, Outlet } from '@tanstack/react-router';

import { ApiError, apiErrorMessage } from '../api/client';

export const Route = createFileRoute('/orgs/$orgSlug')({
  component: Outlet,
  errorComponent: OrgRouteError,
});

function OrgRouteError({ error }: { error: unknown }): React.JSX.Element {
  const isApi = error instanceof ApiError;
  const status = isApi ? error.status : null;
  const heading =
    status === 404
      ? 'Organization not found'
      : status === 403
        ? 'You don’t have access to this organization'
        : 'Something went wrong loading this organization';
  const body = apiErrorMessage(error, 'Try again, or pick a different organization from the home page.');

  return (
    <div className="mx-auto max-w-2xl py-7">
      <p className="text-text-faint text-11 mb-2 uppercase tracking-wider">
        Error
      </p>
      <h1 className="text-text text-28 mb-3 font-semibold">{heading}</h1>
      <p className="text-text-muted mb-5 text-13">{body}</p>
      <Link
        to="/"
        className="bg-accent text-accent-fg border-border-strong rounded-2 hover:bg-accent-hi inline-block border px-3 py-2 text-13 transition-colors"
      >
        Back to home
      </Link>
    </div>
  );
}
